import { EmptyState, RecoveryState } from "@ondit/ui";
import type { ReactNode } from "react";
import { loginUrl } from "~/core/api/index";
import { useI18n, useT } from "~/core/i18n";
import { apiError } from "~/core/query";
import type { BoundaryFallbackProps } from "~/core/ui/boundary";

/**
 * What a screen reads off a query: `useQuery`'s result satisfies it as it
 * stands, so a component handed one needs no adapter.
 */
export interface Reading<T> {
	data: T | undefined;
	error: unknown;
	isFetching: boolean;
	refetch: () => unknown;
}

/**
 * The three states every screen has to handle.
 *
 * Rendering data-or-nothing is the mistake this component exists to prevent: a
 * panel that shows "0 citations" while it is still loading tells a customer
 * their visibility collapsed.
 */
export function Async<T>({
	state,
	children,
	empty,
}: {
	state: Reading<T>;
	children: (data: T) => ReactNode;
	empty?: ReactNode;
}) {
	const t = useT();
	const { locale } = useI18n();
	const error = apiError(state.error);

	// Nothing to draw yet, whichever way a query says so: no answer, or an
	// answer of nothing.
	const none = state.data === undefined || state.data === null;

	if (state.isFetching && none && !error) return <Skeleton />;

	if (error) {
		// An expired session is not an error to retry — it is a sign-in.
		if (error.isUnauthorized) {
			window.location.href = loginUrl(window.location.pathname);
			return <Skeleton />;
		}
		return (
			<RecoveryState
				title={t("state.loadError")}
				description={error.messageKey === "errors.internal" ? t("state.loadErrorBody") : error.localized(locale)}
				retryLabel={t("state.retry")}
				retryingLabel={t("state.retrying")}
				retrying={state.isFetching}
				onRetry={() => void state.refetch()}
			/>
		);
	}

	if (none) return empty ?? <Skeleton />;
	if (Array.isArray(state.data) && state.data.length === 0 && empty) return <>{empty}</>;

	return <>{children(state.data as T)}</>;
}

export function Skeleton() {
	const t = useT();
	return (
		<div className="loading-placeholder" role="status" aria-label={t("state.loading")}>
			<div className="skeleton" aria-hidden="true" />
			<div className="skeleton" aria-hidden="true" />
			<div className="skeleton" aria-hidden="true" />
		</div>
	);
}

/**
 * What a crashed screen shows.
 *
 * The same recovery panel a failed read shows, because it is the same question
 * for the reader — this part did not come up, here is the button. `reset` is
 * the boundary's, so retrying redraws the subtree rather than reloading the
 * document, and the shell around it never unmounted.
 */
export function Crashed({ reset }: BoundaryFallbackProps) {
	const t = useT();
	return (
		<RecoveryState
			title={t("state.error")}
			description={t("state.crashBody")}
			retryLabel={t("state.retry")}
			retryingLabel={t("state.retrying")}
			retrying={false}
			onRetry={reset}
		/>
	);
}

export function Empty({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
	return <EmptyState title={title} description={body} action={action} />;
}
